import React from 'react';
import { css } from '@emotion/css';

import { useNavigate } from 'react-router-dom';
import { Indicator } from 'is24-corecss';
import { BenefitCard } from './benefit/BenefitCard';
import { BenefitDefinition } from '../../../../@types/benefit';
import { useGetBenefitPageName, useGetBenefitsListPage } from '../../../../customHooks/useGetBenefitsListPage.hook';
import { useGetActiveAnnotations } from '../../../../customHooks/useGetActiveAnnotations.hook';
import { BenefitsListPage } from '../../../../@types/benefitsListPage';
import { useClearNotificationMarker } from '../../../../apiHooks/notifications.hook';
import { useBenefits } from '../BenefitsContext';
import { Benefit, COMMON_PLUS_BENEFITS } from '../../../../@types/benefits';
import { BenefitCardStatus } from '../../../../utils/getBenefitCardStatus.util';
import { useGetBenefitCardsStatus } from '../../../../customHooks/useGetBenefitCardsStatus.hook';
import useNavigationPaths from '../../../../customHooks/useNavigationPaths.hook';
import { BenefitsErrorMessage } from '../BenefitErrorMessage';
import { useGetBenefitListError } from '../../../../customHooks/useGetBenefitsListError.hook';
import { useHasWohnenPlusLight } from '@/customHooks/useGetHasProduct.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const benefitPageNames = {
  mieterplus: 'MieterPlus',
  wohnenplus: 'WohnenPlus',
  kaeuferplus: 'KäuferPlus',
};

const styles = {
  flex: {
    display: 'flex',
  },
  headline: {
    display: 'flex',
    fontSize: '24px',
    fontWeight: 400,
    '@media (max-width: 668px)': {
      fontSize: '20px',
      lineHeight: '32px',
    },
  },
  boldPlus: css`
    position: relative;
    font-weight: 800;
    margin-right: 14px;
    white-space: nowrap;
  `,
  plusSvg: css`
    position: absolute;
    height: 60%;
    right: -28%;
    top: -5%;
    width: 35%;
  `,
  customIndicator: css`
    span {
      background-color: var(--color-background-info) !important;
    }
  `,
  gridList: css`
    border-radius: 0 0 8px 8px;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: 56px;
    grid-column-gap: 15px;
    z-index: 4;
    @media only screen and (min-width: 565px) {
      grid-template-rows: 72px;
      grid-template-columns: repeat(2, 1fr);
      grid-row-gap: 15px;
    }
    @media only screen and (min-width: 780px) {
      grid-template-columns: repeat(3, 1fr);
    }
  `,
};

export type BenefitsListProps = {
  benefits: BenefitDefinition[];
};

const benefitsHeadline2 = {
  [BenefitsListPage.MieterPlus]: 'benefitsFilters.headline2.renting',
  [BenefitsListPage.WohnenPlus]: 'benefitsFilters.headline2.living',
  [BenefitsListPage.KauferPlus]: 'benefitsFilters.headline2.buying',
};

export const BenefitsList = ({ benefits }: BenefitsListProps) => {
  const { t } = useIntl();
  const { activeAnnotations, activeAnnotationsCount } = useGetActiveAnnotations();
  const { navigateToNestedPath } = useNavigationPaths();
  const benefitsListPage = useGetBenefitsListPage();
  const navigate = useNavigate();
  const { clearNotificationMarker } = useClearNotificationMarker();
  const { onSelectBenefit } = useBenefits();
  const benefitCardsStatus = useGetBenefitCardsStatus();
  const defaultBenefitCardStatus: BenefitCardStatus = { statusTextTranslationKey: null, iconType: null };
  const lightOnlyCardStatus: BenefitCardStatus = {
    statusTextTranslationKey: 'benefitCardStatus.lightOnly',
    iconType: null,
  };
  const benefit = useGetBenefitPageName();
  const benefitsListError = useGetBenefitListError();
  const hasWohnenPlusLight = useHasWohnenPlusLight();

  const handleBenefitCardClick = React.useCallback(
    ({ redirectTo, benefitId, isUnviewed }: { redirectTo: string; benefitId: Benefit; isUnviewed: boolean }) => {
      if (
        (COMMON_PLUS_BENEFITS.includes(benefitId) || benefitsListPage !== BenefitsListPage.KauferPlus) &&
        isUnviewed
      ) {
        clearNotificationMarker(benefitId);
      }
      navigateToNestedPath(redirectTo);
      onSelectBenefit(benefitId);
    },
    [navigate, onSelectBenefit, clearNotificationMarker, benefitsListPage],
  );

  return (
    <div className='BenefitsList'>
      <div
        role='heading'
        aria-level={2}
        className={`${css(styles.flex)} grid-align-center palm-margin-bottom-xl margin-bottom-l vertical-center-container`}
      >
        <h3 id='benefitsFilters.headline' className={`${css(styles.headline)} margin-bottom-none grid-align-center`}>
          {t({ id: 'benefitsFilters.headline' })}
          &nbsp;
          <PlusBadge variant={benefitsListPage === BenefitsListPage.WohnenPlus ? 'living' : 'search'} logo={true} />
          &nbsp;
          {t({ id: benefitsHeadline2[benefitsListPage] })}
        </h3>
        {!!activeAnnotationsCount && (
          <Indicator
            appearance='numeric'
            color='blue'
            className={`palm-margin-left-m margin-left-s ${styles.customIndicator}`}
          >
            {activeAnnotationsCount}
          </Indicator>
        )}
      </div>
      {benefitsListError && (
        <BenefitsErrorMessage
          text={t(
            {
              id: 'benefitsFilters.errorMessage',
              defaultMessage: 'default Message',
            },
            { benefit: benefitPageNames[benefit] },
          )}
        />
      )}

      <div role='list' aria-labelledby='benefitsFilters.headline' className={styles.gridList}>
        {benefits.map((benefit) => (
          <BenefitCard
            key={benefit.type}
            onClick={() =>
              handleBenefitCardClick({
                redirectTo: benefit?.redirectTo,
                benefitId: benefit.type,
                isUnviewed: activeAnnotations?.includes(benefit.type),
              })
            }
            title={benefit.title}
            defaultTitle={benefit.defaultTitle}
            unviewed={activeAnnotations?.includes(benefit.type)}
            isEntitledForService={benefit?.isEntitledForService}
            benefitCardStatus={
              hasWohnenPlusLight && benefitsListPage === BenefitsListPage.WohnenPlus && !benefit?.isEntitledForService
                ? lightOnlyCardStatus
                : benefitCardsStatus?.[benefit.type as keyof typeof benefitCardsStatus] || defaultBenefitCardStatus
            }
          />
        ))}
      </div>
    </div>
  );
};
